/**
 * Tickets API client
 * Handles all HTTP requests to the tickets endpoints
 */

import {
  BulkUpdateRequest,
  BulkUpdateResponse,
  CreateTicketRequest,
  TicketCategory,
  TicketListResponse,
  TicketPriority,
  TicketResponse,
  TicketStatsResponse,
  TicketStatus,
  UpdateTicketRequest,
} from '@hrms/shared/types';

import { apiClient } from './client';

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TicketQueryParams extends PaginationParams {
  status?: TicketStatus[];
  priority?: TicketPriority[];
  category?: TicketCategory[];
  assignedTo?: string;
  createdBy?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
  slaBreached?: boolean;
  myTickets?: boolean;
  watching?: boolean;
  overdue?: boolean;
  tags?: string[];
}

class TicketsApi {
  private readonly basePath = '/tickets';

  /**
   * Create a new ticket
   */
  async createTicket(data: CreateTicketRequest): Promise<TicketResponse> {
    const response = await apiClient.post(this.basePath, data);
    return response.data;
  }

  /**
   * Get tickets with filters and pagination
   */
  async getTickets(params?: TicketQueryParams): Promise<TicketListResponse> {
    const response = await apiClient.get(this.basePath, { params });
    return response.data;
  }

  /**
   * Get ticket by ID
   */
  async getTicketById(id: string): Promise<TicketResponse> {
    const response = await apiClient.get(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Update ticket
   */
  async updateTicket(id: string, data: UpdateTicketRequest): Promise<TicketResponse> {
    const response = await apiClient.patch(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Assign ticket to a user
   */
  async assignTicket(id: string, userId: string, notes?: string): Promise<TicketResponse> {
    const response = await apiClient.patch(`${this.basePath}/${id}/assign`, {
      assignedTo: userId,
      notes,
    });
    return response.data;
  }

  /**
   * Update ticket status
   */
  async updateTicketStatus(
    id: string,
    status: TicketStatus,
    resolution?: string
  ): Promise<TicketResponse> {
    const response = await apiClient.patch(`${this.basePath}/${id}/status`, {
      status,
      resolution,
    });
    return response.data;
  }

  /**
   * Bulk update tickets
   */
  async bulkUpdateTickets(data: BulkUpdateRequest): Promise<BulkUpdateResponse> {
    const response = await apiClient.patch(`${this.basePath}/bulk`, data);
    return response.data;
  }

  /**
   * Get ticket statistics
   */
  async getTicketStats(params?: TicketQueryParams): Promise<TicketStatsResponse> {
    const response = await apiClient.get(`${this.basePath}/stats`, { params });
    return response.data;
  }

  /**
   * Get my assigned tickets
   */
  async getMyTickets(params?: TicketQueryParams): Promise<TicketListResponse> {
    const response = await apiClient.get(`${this.basePath}/my-tickets`, { params });
    return response.data;
  }

  /**
   * Get tickets I'm watching
   */
  async getWatchingTickets(params?: TicketQueryParams): Promise<TicketListResponse> {
    const response = await apiClient.get(`${this.basePath}/watching`, { params });
    return response.data;
  }

  /**
   * Search tickets
   */
  async searchTickets(query: string, params?: TicketQueryParams): Promise<TicketListResponse> {
    const response = await apiClient.get(`${this.basePath}/search`, {
      params: { ...params, search: query },
    });
    return response.data;
  }

  async exportTickets(
    format: 'csv' | 'excel' | 'pdf',
    filters?: TicketQueryParams
  ): Promise<Blob> {
    try {
      const response = await apiClient.get(`${this.basePath}/export`, {
        params: { ...filters, format },
        responseType: 'blob',
      });

      return response.data;
    } catch (error) {
      const blob = (error as { response?: { data?: unknown } })?.response?.data;

      if (blob instanceof Blob && blob.type === 'application/json') {
        const text = await blob.text();

        let message = 'No records found';

        try {
          const json = JSON.parse(text);
          message = json.message || message;
        } catch {
        // keep default message
        }

        throw new Error(message);
      }

    throw error;
  }
}
}

export const ticketsApi = new TicketsApi();
